import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";

/**
 * Compact horizontal CTA band — dropped between sections to catch a reader
 * whose trust is peaking (e.g. after credibility, after testimonials).
 *
 * Props:
 *   eyebrow: small uppercase tag above the headline (e.g. "Ready when you are")
 *   headline: short serif headline (1 line ideal)
 *   subline:  optional short line under the headline
 *   ctaLabel: button text
 *   onClick:  handler (opens checkout dialog)
 *   variant:  "cream" (default, warm) | "navy" (darker, for high-contrast breaks)
 *   testId:   data-testid for QA
 */
export default function InlineCTA({
    eyebrow,
    headline,
    subline,
    ctaLabel = "Start now",
    onClick,
    variant = "cream",
    testId,
}) {
    const isNavy = variant === "navy";
    return (
        <section
            className={
                isNavy
                    ? "bg-aq-navy text-aq-cream"
                    : "bg-aq-cream-warm text-aq-charcoal"
            }
            data-testid={testId}
        >
            <div className="max-w-5xl mx-auto px-5 md:px-8 py-10 md:py-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6 md:gap-10">
                <div className="max-w-xl">
                    {eyebrow && (
                        <p
                            className={`text-[11px] uppercase tracking-[0.22em] font-semibold mb-2 ${
                                isNavy ? "text-aq-gold-soft" : "text-aq-gold"
                            }`}
                        >
                            {eyebrow}
                        </p>
                    )}
                    <h3
                        className={`font-serif text-2xl md:text-3xl leading-snug ${
                            isNavy ? "text-aq-cream" : "text-aq-navy"
                        }`}
                    >
                        {headline}
                    </h3>
                    {subline && (
                        <p
                            className={`text-sm md:text-base mt-2 leading-relaxed ${
                                isNavy ? "text-aq-cream/70" : "text-aq-muted"
                            }`}
                        >
                            {subline}
                        </p>
                    )}
                </div>
                <Button
                    onClick={onClick}
                    data-testid={`${testId}-button`}
                    className="shrink-0 h-14 px-8 rounded-full bg-aq-gold hover:bg-aq-gold-soft text-aq-navy font-bold text-base shadow-[0_10px_30px_-12px_rgba(200,150,44,0.6)] hover:-translate-y-0.5 transition-all"
                >
                    {ctaLabel}
                    <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
            </div>
        </section>
    );
}
